import React, { useState, useEffect } from "react";
import {
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import ReminderDialog from "./ReminderDialog";
import { useAuth } from "../../../AuthContext";
import API_BASE_URL from "../../../config/api";

// ensure axios always includes cookies in requests
axios.defaults.withCredentials = true;

const Appointment = () => {
  const [appointments, setAppointments] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [tab, setTab] = useState(0);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTimeSlot, setScheduledTimeSlot] = useState("");
  const [custNotes, setCustNotes] = useState("");
  const [sellerAssign, setSellerAssign] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [assignSellerOpen, setAssignSellerOpen] = useState(false);
  const [bookedSlots, setBookedSlots] = useState({});

  // get loggedin user data
  const { user } = useAuth();
  const isSupervisor = user?.role === "supervisor";

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const timeSlots = [
    "10:00 AM - 11:00 AM",
    "11:30 AM - 12:30 PM",
    "1:00 PM - 2:00 PM",
    "2:30 PM - 3:30 PM",
    "4:00 PM - 5:00 PM",
    "5:30 PM - 6:30 PM",
    "7:00 PM - 8:00 PM",
    "8:30 PM - 9:30PM",
  ];

  // fetch booked time slots for the selected date and location
  const fetchBookedTimeSlots = async (date, location) => {
    if (!date || !location) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/getBookedTimeSlots.php?date=${date}&location=${location}`
      );

      setBookedSlots(response.data || {});
    } catch (error) {
      console.error("Error fetching booked time slots:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
      }
      setBookedSlots({}); // Reset in case of an error
    }
  };

  useEffect(() => {}, [bookedSlots]); // Logs state whenever it updates

  const handleTimeSlotDisable = (slot) => {
    const currentDate = new Date();
    const selectedDate = new Date(scheduledDate);

    // Disable all slots if selected date is in the past
    if (selectedDate < new Date().setHours(0, 0, 0, 0)) {
      return true;
    }

    // If selected date is today, disable past time slots
    if (selectedDate.toDateString() === currentDate.toDateString()) {
      const currentHours = currentDate.getHours();
      const currentMinutes = currentDate.getMinutes();

      // Convert slot start time to 24-hour format
      const [startTime] = slot.split(" - ");
      let [hours, minutes] = startTime.split(":");
      const period = startTime.includes("PM") ? "PM" : "AM";

      // Convert to 24-hour format
      hours = parseInt(hours, 10);
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      minutes = parseInt(minutes, 10);

      // Disable past time slots for today
      return (
        hours < currentHours ||
        (hours === currentHours && minutes < currentMinutes)
      );
    }

    return false; // Future dates have all slots available
  };

  // Fetch booked slots when date is selected
  useEffect(() => {
    if (scheduledDate && selectedAppointment?.location) {
      fetchBookedTimeSlots(scheduledDate, selectedAppointment.location);
    }
  }, [scheduledDate, selectedAppointment]);

  useEffect(() => {
    fetchAppointments("scheduled");
    fetchSellers();
  }, []);

  // Fetch Sellers for the dropdown
  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/fetchSellers.php`);
      setSellers(response.data);
    } catch (error) {
      console.error("Error fetching sellers", error);
    }
  };

  // Fetch appointments from PHP backend
  const fetchAppointments = async (status) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin/fetchAllAppointments.php?status=${status}`,
        { withCredentials: true } // include session cookie
      );

      // mark appointment as new if their created_at is AFTER the last seen timestamp
      const lastSeenTimeStamp = parseInt(localStorage.getItem("lastSeen")) || 0;
      const updatedAppointments = response.data.map((appointment) => ({
        ...appointment,
        isNew: moment(appointment.created_at).isAfter(
          moment(parseInt(lastSeenTimeStamp))
        ),
      }));

      setAppointments(updatedAppointments);
    } catch (error) {
      console.error("Error fetching appointments", error);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    if (newValue === 0) fetchAppointments("scheduled");
    if (newValue === 1) fetchAppointments("completed");
  };

  // Handle assign seller action
  const handleAssignSeller = async () => {
    setAssignSellerOpen(false);

    if (!isSupervisor && sellerAssign !== String(user.id)) {
      Swal.fire("Permission Denied", "You can only assign yourself.", "error");
      return;
    }

    const selectedSeller = sellers.find(
      (seller) => String(seller.id) === sellerAssign
    );
    const sellerNameToShow = selectedSeller
      ? selectedSeller.username
      : "Unknown Seller";

    if (!selectedSeller) {
      Swal.fire("Alert", "Please select a seller to assign.", "warning");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to assign Seller ${sellerNameToShow} to this appointment.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.put(
          `${API_BASE_URL}/admin/assignSellerScript.php`,
          JSON.stringify({
            id: selectedAppointment.id,
            seller_assign: sellerAssign, // Sends correct users.id (Primary Key)
          }),
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          }
        );

        if (response.data.error) {
          Swal.fire("Error", response.data.error, "error");
          return;
        }

        if (response.data.success) {
          Swal.fire(
            "Assigned!",
            `Seller ${sellerNameToShow} has been assigned to this appointment.`,
            "success"
          ).then(() => window.location.reload());
        } else {
          Swal.fire("Alert", response.data.message, "warning");
        }
      } catch (error) {
        console.error("Error assigning seller", error);
        Swal.fire(
          "Error",
          "Failed to assign seller to the appointment.",
          "error"
        );
      }
    }
  };

  // handle rescheduling appointment
  const handleReschedule = async () => {
    if (!scheduledDate || !scheduledTimeSlot || !selectedAppointment) return;

    setRescheduleOpen(false);

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to reschedule this appointment to ${scheduledDate} at ${scheduledTimeSlot}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reschedule it!",
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: "Processing...",
        text: "Please wait while we reschedule the appointment.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const response = await axios.put(
          `${API_BASE_URL}/admin/rescheduleAppointment.php`,
          JSON.stringify({
            id: selectedAppointment.id,
            scheduled_date: scheduledDate,
            scheduled_timeslot: scheduledTimeSlot,
            notes_for_cust: custNotes,
          }),
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true, // ensure session cookies are included
          }
        );

        Swal.close();

        if (response.data.error) {
          Swal.fire("Error", response.data.error, "error");
          return;
        }

        Swal.fire(
          "Rescheduled!",
          "Appointment rescheduled and an email has been sent to the customer successfully.",
          "success"
        ).then(() => window.location.reload());
      } catch (error) {
        Swal.close();
        console.error("Error rescheduling appointment", error);
        Swal.fire("Error", "Failed to reschedule the appointment.", "error");
      }
    }
  };

  // handle close appointment
  const handleCloseAppointment = async (appointmentId) => {
    const firstResponse = await Swal.fire({
      title: "Did the customer appear on this appointment?",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
      cancelButtonText: "Cancel",
    });

    if (firstResponse.isConfirmed) {
      // If Yes, close the appointment normally
      try {
        const response = await axios.put(
          `${API_BASE_URL}/admin/closeAppointment.php`,
          JSON.stringify({
            id: appointmentId,
            reason: "", // No reason needed if the customer appeared
          }),
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true, // ensure session cookies are included
          }
        );

        if (response.data.error) {
          Swal.fire("Error", response.data.error, "error");
        } else {
          Swal.fire(
            "Closed!",
            "The appointment has been marked as completed.",
            "success"
          );
          fetchAppointments("scheduled"); // Refresh scheduled appointments
        }
      } catch (error) {
        console.error("Error closing appointment", error);
        Swal.fire("Error", "Failed to close the appointment.", "error");
      }
    } else if (firstResponse.isDenied) {
      // If No, ask for a final confirmation
      const secondResponse = await Swal.fire({
        title: "Are you sure you want to close this appointment?",
        text: "This action will require you to enter a reason.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, close it",
        cancelButtonText: "No, cancel",
      });

      if (secondResponse.isConfirmed) {
        // Ask the seller for the reason
        const { value: reason } = await Swal.fire({
          title: "Enter Reason",
          input: "textarea",
          inputLabel: "Please provide a reason for closing this appointment.",
          inputPlaceholder: "Enter reason here...",
          inputAttributes: { required: "true" },
          showCancelButton: true,
          confirmButtonText: "Confirm",
        });

        if (reason) {
          try {
            const response = await axios.put(
              `${API_BASE_URL}/admin/closeAppointment.php`,
              JSON.stringify({
                id: appointmentId,
                reason: reason, // Send reason to backend
              }),
              {
                headers: { "Content-Type": "application/json" },
              }
            );

            if (response.data.error) {
              Swal.fire("Error", response.data.error, "error");
            } else {
              Swal.fire(
                "Closed!",
                "The appointment has been closed with a reason.",
                "success"
              );
              fetchAppointments("scheduled"); // Refresh scheduled appointments
            }
          } catch (error) {
            console.error("Error closing appointment", error);
            Swal.fire("Error", "Failed to close the appointment.", "error");
          }
        }
      }
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Show ReminderDialog only once when user logs in */}
      <ReminderDialog showOnLoginOnly={true} />

      <Typography
        variant="h4"
        sx={{ marginBottom: 2, textAlign: "center", fontWeight: "bold" }}
      >
        Product Trial Appointments
      </Typography>
      {/* Tabs for scheduled, and completed */}
      <Tabs
        value={tab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Scheduled" />
        <Tab label="Completed" />
      </Tabs>
      {/* Scheduled Tab */}
      <TableContainer
        component={Paper}
        sx={{ marginTop: 3, overflowX: "auto" }}
      >
        <Table sx={{ whiteSpace: "nowrap" }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ textAlign: "right" }}>
                <b>No.</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Created At</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Name</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Email</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Phone</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Customer Remarks</b>
              </TableCell>

              <TableCell style={{ textAlign: "center" }}>
                <b>Scheduled Date</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Scheduled Time Slot</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Assigned To</b>
              </TableCell>

              {/* show reason closed column only for status completed */}
              {tab === 1 && (
                <TableCell style={{ textAlign: "center" }}>
                  <b>Reason Closed</b>
                </TableCell>
              )}

              <TableCell style={{ textAlign: "center" }}>
                <b>Updated By</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Updated At</b>
              </TableCell>
              <TableCell style={{ textAlign: "center" }}>
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No appointments found
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((appointment, index) => (
                <TableRow key={appointment.id}>
                  <TableCell style={{ textAlign: "right" }}>
                    {appointment.isNew && (
                      <span
                        style={{
                          backgroundColor: "black",
                          color: "white",
                          fontSize: "10px",
                          padding: "3px 6px",
                          marginRight: "5px",
                        }}
                      >
                        NEW
                      </span>
                    )}{" "}
                    {index + 1}
                  </TableCell>
                  <TableCell>
                    {moment(appointment.created_at).format("YYYY-MM-DD HH:mm")}
                  </TableCell>
                  <TableCell>{appointment.name} </TableCell>
                  <TableCell>{appointment.email}</TableCell>
                  <TableCell>{appointment.phone}</TableCell>
                  <TableCell>{appointment.remarks}</TableCell>
                  <TableCell>
                    {appointment.scheduled_date
                      ? appointment.scheduled_date
                      : "Not Scheduled"}
                  </TableCell>
                  <TableCell>
                    {appointment.scheduled_timeslot
                      ? appointment.scheduled_timeslot
                      : "Not Scheduled"}
                  </TableCell>
                  <TableCell>{appointment.assign_seller_name ?? "-"}</TableCell>

                  {/* Show "Reason Closed" data only for Completed appointments */}
                  {tab === 1 && (
                    <TableCell>{appointment.close_reason ?? "-"}</TableCell>
                  )}

                  <TableCell>
                    {appointment.updator_seller_name ?? "-"}
                  </TableCell>
                  <TableCell>{appointment.updated_at ?? "-"}</TableCell>
                  <TableCell>
                    {/* Reschedule button for upcoming tab */}
                    {tab === 0 && (
                      <>
                        {/* Button - Assign Seller */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setSelectedAppointment(appointment);
                            setSellerAssign(
                              isSupervisor
                                ? appointment.seller_id
                                  ? String(appointment.seller_id)
                                  : "" // Keep existing seller ID if present
                                : String(user?.id) // Sellers can only select themselves
                            );
                            setAssignSellerOpen(true);
                          }}
                        >
                          Assign
                        </Button>

                        {/* Button - Reschedule Appointment */}
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => {
                            setSelectedAppointment(appointment);
                            setScheduledDate(appointment.scheduled_date);
                            setScheduledTimeSlot(
                              appointment.scheduled_timeslot
                            );
                            setCustNotes(appointment.notes_for_cust);
                            setRescheduleOpen(true);
                          }}
                          sx={{ mx: 2 }} // Adds margin on both left and right
                        >
                          Reschedule
                        </Button>

                        {/* Button - Close Appointment */}
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleCloseAppointment(appointment.id)}
                        >
                          Close
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)}>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            fullWidth
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            sx={{ mt: 2 }}
            inputProps={{ min: today }}
          />

          <TextField
            select
            label="Select Time Slot"
            fullWidth
            value={scheduledTimeSlot}
            onChange={(e) => setScheduledTimeSlot(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="" disabled>
              Select Time Slot
            </option>
            {timeSlots.map((slot, index) => {
              const bookedCount = bookedSlots[slot] || 0;
              const isFull = bookedCount >= 2; // Assuming max 2 bookings per slot
              const isPast = handleTimeSlotDisable(slot);

              return (
                <option key={index} value={slot} disabled={isFull || isPast}>
                  {slot}{" "}
                  {isFull
                    ? "(Full)"
                    : isPast
                    ? "(Unavailable)"
                    : `(${bookedCount} Booked)`}
                </option>
              );
            })}
          </TextField>

          <TextField
            label="Notes for Customer"
            fullWidth
            multiline
            rows={2}
            value={custNotes ?? ""}
            onChange={(e) => setCustNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReschedule}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Seller Dialog */}
      <Dialog
        open={assignSellerOpen}
        onClose={() => setAssignSellerOpen(false)}
      >
        <DialogTitle>Assign Seller</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Select Seller"
            fullWidth
            value={sellerAssign}
            onChange={(e) => setSellerAssign(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            {isSupervisor ? (
              // Supervisor can assign any seller
              <>
                <option value="" disabled>
                  Select Seller
                </option>
                {sellers.map((seller) => (
                  <option key={seller.id} value={String(seller.id)}>
                    {seller.username}
                  </option>
                ))}
              </>
            ) : (
              // Seller can only assign themselves
              <option value={String(user?.id)}>{user?.name}</option>
            )}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignSellerOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignSeller}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointment;
